import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Modal } from "@/ui";
import { ConfirmNote } from "./ConfirmNote";
import type { DeleteTarget } from "./deleteTarget";
import { useSave } from "./useSave";

/** 删除误建实体的确认（docs/04 2.2，决策 D180）：每次打开都重新读删除检查。有阻止原因时逐条列出、只能关闭；
 * 没有时写明会一并删除的从属配置，确认后删除。检查读取中或失败都不能删，服务端删除时还会再查一次。 */
export function EntityDeleteModal({ target, onClose, onDeleted }: { target: DeleteTarget; onClose: () => void; onDeleted: () => void }) {
  const check = useQuery({ queryKey: ["delete-check", target.noun, target.name], queryFn: target.check, staleTime: 0, gcTime: 0 });
  const save = useSave(target.remove, { invalidate: target.invalidate, message: `已删除${target.noun} ${target.name}`, onSaved: onDeleted });
  const blockers = check.data?.blockers ?? [];
  const ready = !!check.data && !check.isFetching && !check.error;
  const canDelete = ready && blockers.length === 0;
  return (
    <Modal open title={`删除${target.noun} ${target.name}`} onClose={onClose}
      footer={save.warning ? <Button onClick={onDeleted}>关闭</Button> : <><Button data-autofocus onClick={onClose}>取消</Button>
        <Button variant="danger" disabled={!canDelete || save.saving} onClick={() => save.save(undefined)}>删除</Button></>}>
      {check.error && <Alert tone="danger" title="删除检查读取失败">{check.error.message}，暂时不能删除。</Alert>}
      {save.error && <Alert tone="danger" title="删除失败">{save.error}</Alert>}
      {save.warning && <Alert tone="warning" title="已删除，后续同步未完成">{save.warning}</Alert>}
      {!ready && !check.error && <ConfirmNote>正在检查能否删除。</ConfirmNote>}
      {ready && blockers.length > 0 && (
        <Alert tone="warning" title="不能删除">
          <ul className="ax-reasons">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
          用过的{target.noun}只能停用；被引用的先解除引用。
        </Alert>
      )}
      {canDelete && !save.warning && (
        <ConfirmNote>
          删除后不能恢复，审计保留删除前的内容。{check.data!.cascades.length ? `将一并删除：${check.data!.cascades.join("、")}。` : ""}
        </ConfirmNote>
      )}
    </Modal>
  );
}
